import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useCustomerDetailData } from "@/hooks/useCustomerDetailData";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Loader2 } from "lucide-react";

import CustomerHeader from "@/components/admin/customer-detail/CustomerHeader";
import CustomerQuickStats from "@/components/admin/customer-detail/CustomerQuickStats";
import CustomerActions from "@/components/admin/customer-detail/CustomerActions";
import CustomerEditDialog from "@/components/admin/customer-detail/CustomerEditDialog";

// Lazy-loaded tab content
const OverviewTab = lazy(() => import("@/components/admin/customer-detail/tabs/OverviewTab"));
const BookingsTab = lazy(() => import("@/components/admin/customer-detail/tabs/BookingsTab"));
const PaymentsTab = lazy(() => import("@/components/admin/customer-detail/tabs/PaymentsTab"));
const MembershipTab = lazy(() => import("@/components/admin/customer-detail/tabs/MembershipTab"));
const ProfileTab = lazy(() => import("@/components/admin/customer-detail/tabs/ProfileTab"));
const LeaguesTab = lazy(() => import("@/components/admin/customer-detail/tabs/LeaguesTab"));
const TrainingTab = lazy(() => import("@/components/admin/customer-detail/tabs/TrainingTab"));
const CommunicationTab = lazy(() => import("@/components/admin/customer-detail/tabs/CommunicationTab"));
const AdminTab = lazy(() => import("@/components/admin/customer-detail/tabs/AdminTab"));

const TabFallback = (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
  </div>
);

export default function CustomerDetailPage() {
  const { t } = useTranslation("admin");
  const { id: customerId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isEnabled } = useFeatureFlags();
  const organizationId = organization?.id;

  // Fetch the full customer record
  const { data: customer, isLoading: customerLoading } = useQuery({
    queryKey: ["customer", customerId, "detail"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("id", customerId!)
        .single();
      if (error) throw error;
      return data as Record<string, unknown> & {
        id: string;
        full_name: string | null;
        email: string;
        phone: string | null;
        customer_number: number | null;
        created_at: string;
        user_id: string | null;
        level: number | null;
        avatar_url?: string | null;
        blocked?: boolean;
        notes?: string | null;
      };
    },
    enabled: !!customerId,
  });

  const userId = customer?.user_id ?? null;

  const {
    stats,
    statsLoading,
    memberships,
    membershipsLoading,
    upcomingBookings,
    upcomingBookingsLoading,
    useBookingHistory,
    usePaymentHistory,
    notes,
    notesLoading,
    addNote,
    toggleBlock,
    deleteCustomer,
  } = useCustomerDetailData(customerId, userId, organizationId);

  const activeMembership = memberships.find(
    (m) => m.status === "active" || m.status === "paused",
  );

  const creditsEnabled = isEnabled("credits") || isEnabled("wallet");

  const [editOpen, setEditOpen] = useState(false);

  const handleClose = () => {
    navigate("/manager/customers");
  };

  if (customerLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-sm text-muted-foreground">{t("customerDetail.notFound")}</p>
        <Link
          to="/manager/customers"
          className="text-sm text-primary hover:underline flex items-center gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("customerDetail.backToCustomers")}
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-28">
      {/* Back link */}
      <Link
        to="/manager/customers"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4 mt-2"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("customerDetail.backToCustomers")}
      </Link>

      {/* Header */}
      <CustomerHeader
        customer={customer}
        membershipStatus={activeMembership?.status ?? null}
        onEdit={() => setEditOpen(true)}
      />

      <Separator className="my-4" />

      {/* Quick Stats */}
      <CustomerQuickStats stats={stats} isLoading={statsLoading} />
      <Separator className="my-4" />

      {/* Tabs */}
      <Tabs defaultValue="overview" className="mt-4">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview">{t("customerDetail.tabs.overview")}</TabsTrigger>
          <TabsTrigger value="bookings">{t("customerDetail.tabs.bookings")}</TabsTrigger>
          <TabsTrigger value="payments">{t("customerDetail.tabs.payments")}</TabsTrigger>
          <TabsTrigger value="membership">{t("customerDetail.tabs.membership")}</TabsTrigger>
          <TabsTrigger value="profile">{t("customerDetail.tabs.profile")}</TabsTrigger>
          <TabsTrigger value="leagues">{t("customerDetail.tabs.leagues")}</TabsTrigger>
          <TabsTrigger value="training">{t("customerDetail.tabs.training")}</TabsTrigger>
          <TabsTrigger value="communication">{t("customerDetail.tabs.communication")}</TabsTrigger>
          <TabsTrigger value="admin">{t("customerDetail.tabs.admin")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Suspense fallback={TabFallback}>
            <OverviewTab
              customer={customer}
              userId={userId}
              organizationId={organizationId}
              stats={stats}
              memberships={memberships}
              membershipsLoading={membershipsLoading}
              upcomingBookings={upcomingBookings}
              upcomingBookingsLoading={upcomingBookingsLoading}
              useBookingHistory={useBookingHistory}
              usePaymentHistory={usePaymentHistory}
              notes={notes}
              notesLoading={notesLoading}
              addNote={addNote}
              creditsEnabled={creditsEnabled}
            />
          </Suspense>
        </TabsContent>

        <TabsContent value="bookings">
          <Suspense fallback={TabFallback}>
            <BookingsTab
              useBookingHistory={useBookingHistory}
              stats={stats}
            />
          </Suspense>
        </TabsContent>

        <TabsContent value="payments">
          <Suspense fallback={TabFallback}>
            <PaymentsTab usePaymentHistory={usePaymentHistory} />
          </Suspense>
        </TabsContent>

        <TabsContent value="membership">
          <Suspense fallback={TabFallback}>
            <MembershipTab
              memberships={memberships}
              membershipsLoading={membershipsLoading}
              creditsEnabled={creditsEnabled}
              userId={userId}
              customerId={customer.id}
              organizationId={organizationId}
            />
          </Suspense>
        </TabsContent>

        <TabsContent value="profile">
          <Suspense fallback={TabFallback}>
            <ProfileTab
              userId={userId}
              customer={customer}
              onEditCustomer={() => setEditOpen(true)}
            />
          </Suspense>
        </TabsContent>

        <TabsContent value="leagues">
          <Suspense fallback={TabFallback}>
            <LeaguesTab userId={userId} />
          </Suspense>
        </TabsContent>

        <TabsContent value="training">
          <Suspense fallback={TabFallback}>
            <TrainingTab userId={userId} />
          </Suspense>
        </TabsContent>

        <TabsContent value="communication">
          <Suspense fallback={TabFallback}>
            <CommunicationTab customer={customer} />
          </Suspense>
        </TabsContent>

        <TabsContent value="admin">
          <Suspense fallback={TabFallback}>
            <AdminTab
              customerId={customer.id}
              organizationId={organizationId}
              customer={customer}
              notes={notes}
              notesLoading={notesLoading}
              addNote={addNote}
              toggleBlock={toggleBlock}
              deleteCustomer={deleteCustomer}
              onClose={handleClose}
            />
          </Suspense>
        </TabsContent>
      </Tabs>

      {/* Sticky action footer */}
      <CustomerActions
        blocked={!!customer.blocked}
        toggleBlock={toggleBlock}
        deleteCustomer={deleteCustomer}
        onClose={handleClose}
      />

      {/* Edit dialog */}
      <CustomerEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        customer={customer}
      />
    </div>
  );
}
